import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { faker } from "@faker-js/faker";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { unwrapConnection } from "@utils/utils";
import React, { useCallback, useEffect, useState } from "react";

import { Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiTreatmentPlanTemplateFieldFieldTypeChoices,
  ApiTreatmentPlanTemplateFieldTypeChoices,
  TreatmentPlanTemplateType,
} from "@api/graphql/types-and-hooks";
import { SectionDivider } from "@pages/Templates/components/SectionDivider/SectionDivider";
import { TreatmentPlanTemplateEditorAddButton } from "@pages/Templates/components/TreatmentPlanTemplateEditor/TreatmentPlanTemplateEditorAddButton";
import { TreatmentPlanTemplateEditorDomain } from "@pages/Templates/components/TreatmentPlanTemplateEditor/TreatmentPlanTemplateEditorDomain";
import { TreatmentPlanTemplateEditorFieldList } from "@pages/Templates/components/TreatmentPlanTemplateEditor/TreatmentPlanTemplateEditorFieldList";

// --- Types ---

export interface TemplateFieldState {
  id: string;
  title: string;
  fieldType: string;
  rankOrder: number;
  isRequired: boolean;
  isEditable: boolean;
  isEnabled: boolean;
  fieldIdentifier: string;
  content?: string;
  type: string;
  reportGoalField: boolean;
  reportTargetField: boolean;
}

export interface DomainState {
  id: string;
  name: string;
  description?: string;
  rankOrder: number;
  templateFields: TemplateFieldState[];
}

export interface TreatmentPlanTemplateState {
  defaultFields: TemplateFieldState[];
  domains: DomainState[];
}

interface TreatmentPlanTemplateEditorProps {
  treatmentPlanTemplate: TreatmentPlanTemplateType;
  readonly: boolean;
  onChange: () => void;
  onUpdate: (data: TreatmentPlanTemplateState) => void;
}

// --- Helper: parse query data into local state --

function parseFieldNode(
  f: {
    id: string;
    title?: string | null;
    fieldIdentifier: string;
    fieldType: string;
    rankOrder: number;
    isRequired: boolean;
    isEditable: boolean;
    isEnabled: boolean;
    content?: string | null;
    type: string;
    reportGoalField: boolean;
    reportTargetField: boolean;
  },
  fIndex: number,
): TemplateFieldState {
  return {
    id: f.id,
    title: f.title || "",
    fieldType: f.fieldType || ApiTreatmentPlanTemplateFieldFieldTypeChoices.SimpleText,
    rankOrder: f.rankOrder ?? fIndex + 1,
    isRequired: f.isRequired ?? false,
    isEditable: f.isEditable ?? true,
    isEnabled: f.isEnabled ?? true,
    content: f.content ?? undefined,
    type: f.type || ApiTreatmentPlanTemplateFieldTypeChoices.Goal,
    reportGoalField: f.reportGoalField ?? false,
    reportTargetField: f.reportTargetField ?? false,
    fieldIdentifier: f.fieldIdentifier ?? "",
  };
}

function parseTemplateData(data: TreatmentPlanTemplateType): {
  defaultFields: TemplateFieldState[];
  domains: DomainState[];
} {
  const allTemplateFields = unwrapConnection(data?.templateFields) ?? [];
  const defaultFields = allTemplateFields
    .filter((f) => !f.domain)
    .map(parseFieldNode)
    .sort((a, b) => a.rankOrder - b.rankOrder);

  const domains = (unwrapConnection(data?.domains) ?? [])
    .map((node, index) => {
      const fields = unwrapConnection(node.templateFields) ?? [];
      const templateFields = fields.map(parseFieldNode).sort((a, b) => a.rankOrder - b.rankOrder);

      return {
        id: node.id,
        name: node.name,
        description: node.description ?? undefined,
        rankOrder: node.rankOrder ?? index + 1,
        templateFields,
      };
    })
    .sort((a, b) => a.rankOrder - b.rankOrder);

  return { defaultFields, domains };
}

// --- Main Component ---

export const TreatmentPlanTemplateEditor: React.FC<TreatmentPlanTemplateEditorProps> = ({
  treatmentPlanTemplate,
  readonly,
  onChange,
  onUpdate,
}) => {
  const [defaultFields, setDefaultFields] = useState<TemplateFieldState[]>(
    () => parseTemplateData(treatmentPlanTemplate).defaultFields,
  );
  const [domains, setDomains] = useState<DomainState[]>(() => parseTemplateData(treatmentPlanTemplate).domains);

  useEffect(() => {
    const parsed = parseTemplateData(treatmentPlanTemplate);
    setDefaultFields(parsed.defaultFields);
    setDomains(parsed.domains);
  }, [treatmentPlanTemplate]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const emitUpdate = useCallback(
    (newDefaultFields: TemplateFieldState[], newDomains: DomainState[]) => {
      setDefaultFields(newDefaultFields);
      setDomains(newDomains);
      onUpdate({ defaultFields: newDefaultFields, domains: newDomains });
      onChange();
    },
    [onUpdate, onChange],
  );

  const handleDefaultFieldsUpdate = (newFields: TemplateFieldState[]) => {
    emitUpdate(newFields, domains);
  };

  const handleDomainDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = domains.findIndex((d) => d.id === active.id);
      const newIndex = domains.findIndex((d) => d.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(domains, oldIndex, newIndex).map((d, i) => ({ ...d, rankOrder: i + 1 }));
        emitUpdate(defaultFields, reordered);
      }
    }
  };

  const handleAddDomain = () => {
    const newDomain: DomainState = {
      id: `temp-${faker.string.uuid()}`,
      name: "New Domain",
      rankOrder: domains.length + 1,
      templateFields: [],
    };
    emitUpdate(defaultFields, [...domains, newDomain]);
  };

  const handleUpdateDomain = (updatedDomain: DomainState) => {
    const updated = domains.map((d) => (d.id === updatedDomain.id ? updatedDomain : d));
    emitUpdate(defaultFields, updated);
  };

  const handleRemoveDomain = (domainId: string) => {
    const filtered = domains.filter((d) => d.id !== domainId).map((d, i) => ({ ...d, rankOrder: i + 1 }));
    emitUpdate(defaultFields, filtered);
  };

  const defaultGoalFields = defaultFields.filter((f) => f.type === ApiTreatmentPlanTemplateFieldTypeChoices.Goal);
  const defaultTargetFields = defaultFields.filter((f) => f.type === ApiTreatmentPlanTemplateFieldTypeChoices.Target);

  return (
    <div>
      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4 flex gap-2 items-start text-xs">
        <InformationCircleIcon className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
        <div className="flex flex-col gap-1">
          <div className="font-semibold text-blue-900">Configure Treatment Plan</div>
          <div className="text-blue-800">
            Create programming domains (e.g., Skill Acquisition, Behavior Reduction) and customize their fields. Default
            fields apply to all domains. Add custom fields per domain, reorder by dragging, and toggle required status
            as needed. Default fields cannot be edited.
          </div>
        </div>
      </div>

      {/* Default Fields */}
      <SectionDivider label="Default" />
      <Small displayType="loud" colorType="secondary" className="mb-2 block">
        Goal
      </Small>
      <TreatmentPlanTemplateEditorFieldList
        fields={defaultGoalFields}
        onUpdateFields={(updated) => {
          handleDefaultFieldsUpdate([...updated, ...defaultTargetFields]);
        }}
        readonly={readonly}
        addLabel="Add Default Goal Field"
        fieldType={ApiTreatmentPlanTemplateFieldTypeChoices.Goal}
      />
      <Small displayType="loud" colorType="secondary" className="mb-2 block mt-6">
        Target
      </Small>
      <TreatmentPlanTemplateEditorFieldList
        fields={defaultTargetFields}
        onUpdateFields={(updated) => {
          handleDefaultFieldsUpdate([...defaultGoalFields, ...updated]);
        }}
        readonly={readonly}
        addLabel="Add Default Target Field"
        fieldType={ApiTreatmentPlanTemplateFieldTypeChoices.Target}
      />

      {/* Domains */}
      <SectionDivider label="Domains" />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDomainDragEnd}>
        <SortableContext items={domains.map((d) => d.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {domains.length === 0 ? (
              <div className="text-text-secondary text-center rounded-lg bg-white border border-limestone-200 p-4">
                <Small>
                  <em>No domains configured. Add your first domain below</em>
                </Small>
              </div>
            ) : (
              domains.map((domain) => (
                <TreatmentPlanTemplateEditorDomain
                  key={domain.id}
                  domain={domain}
                  onUpdateDomain={handleUpdateDomain}
                  onRemoveDomain={() => handleRemoveDomain(domain.id)}
                  readonly={readonly}
                />
              ))
            )}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add Domain */}
      {!readonly && (
        <TreatmentPlanTemplateEditorAddButton label="Add Domain" onClick={handleAddDomain} className="mt-4" />
      )}
    </div>
  );
};
